import { colors, fontWeights } from 'ui/theme';
import { userIcon, usersExitedIcon, usersEnteredIcon } from '../../../icons';
import { ItemState } from '../../../constants';
import {
  DEFAULT_ACCENT_COLOR,
  DEFAULT_BACKGROUND_ACCENT_COLOR,
  DEFAULT_BORDER_ACCENT_COLOR,
  DEFAULT_FONT,
  DEFAULT_NODE_SIZE,
  HORIZONTAL_PADDING,
  ICON_BACKGROUND_SIZE,
  ICON_SIZE,
  NODE_BACKGROUND_COLOR,
  deleteStyles,
  deleteStylesActive,
  fontConfig,
  shapeNames,
  groupIds,
  noteIndicatorStyles,
} from '../../constants';

const baseNumUsersStyles = {
  fill: colors.white,
  stroke: colors.grey25,
  padding: [3, 6],
  lineWidth: 1,
};

const baseNumUserIconStyles = {
  padding: [3, 4],
  // not an actual style property, but needed for position calculation
  iconWidth: 16,
};

const baseNumUsersTextStyles = {
  padding: 2,
  ...fontConfig.fs13,
  ...fontConfig.default,
  textBaseline: 'bottom',
};

export const getNodeDefaultOptions = () => {
  return {
    size: DEFAULT_NODE_SIZE,
    defaultTitle: '',
    icon: '',
    accentColorBackground: DEFAULT_BACKGROUND_ACCENT_COLOR,
    accentColor: DEFAULT_ACCENT_COLOR,
    activeBorderColor: DEFAULT_BORDER_ACCENT_COLOR,
    padding: HORIZONTAL_PADDING,
    [shapeNames.wrapper]: {
      fill: NODE_BACKGROUND_COLOR,
      stroke: colors.grey25,
      radius: 8,
      lineWidth: 1,
    },
    [groupIds.anchor]: {
      maxWidth: 134,
      padding: [0, 2],
    },
    [shapeNames.anchor]: {
      fill: NODE_BACKGROUND_COLOR,
      stroke: colors.grey50,
      r: 7,
      lineWidth: 1,
    },
    [shapeNames.anchorTitle]: {
      textAlign: 'center',
      opacity: 1,
      fontSize: 12,
      lineHeight: 16,
      fontFamily: DEFAULT_FONT,
    },
    [shapeNames.nodeId]: {
      fill: colors.grey80,
      textAlign: 'left',
      textBaseline: 'bottom',
      fontWeight: fontWeights.regular,
      opacity: 0.5,
      x: 0,
      y: -5,
      ...fontConfig.fs12,
    },
    [shapeNames.iconBackground]: {
      width: ICON_BACKGROUND_SIZE,
      height: ICON_BACKGROUND_SIZE,
      radius: 6,
      margin: [0, 12, 0, 0],
    },
    [shapeNames.icon]: {
      width: ICON_SIZE,
      height: ICON_SIZE,
      opacity: 1,
    },
    [shapeNames.title]: {
      fontWeight: fontWeights.medium,
      fill: colors.textPrimary,
      y: 13,
      opacity: 1,
      ...fontConfig.fs14,
      ...fontConfig.default,
      textBaseline: 'top',
    },
    [shapeNames.descBottom]: {
      fontWeight: fontWeights.regular,
      fill: colors.textSecondary,
      opacity: 1,
      offset: [6, 0, 0],
      ...fontConfig.fs14,
      ...fontConfig.default,
      textBaseline: 'top',
    },
    [shapeNames.divider]: {
      height: 3,
      padding: [4, 0, 4, 0],
      fill: colors.grey15,
      radius: 0,
    },
    [shapeNames.footerText]: {
      fontWeight: fontWeights.medium,
      ...fontConfig.fs14,
      ...fontConfig.default,
      textBaseline: 'top',
      fill: colors.textSecondary,
      offset: [25, 0, 0, 0],
    },
    [shapeNames.footerContentWrapper]: {
      padding: [4, 5],
    },
    [shapeNames.childNode]: {
      padding: [8, 5, 0],
    },
    [shapeNames.warningMessage]: {
      fontWeight: fontWeights.regular,
      fill: colors.textDisabled,
      y: 33,
    },
    [shapeNames.numUsersIcon]: {
      ...baseNumUserIconStyles,
      opacity: 1,
      cursor: 'pointer',
      padding: [3, 4],
      img: userIcon,
    },
    [shapeNames.numUsersEnteredIcon]: {
      ...baseNumUserIconStyles,
      padding: [1, 5, 0, 2],
      img: usersEnteredIcon,
    },
    [shapeNames.numUsersExitedIcon]: {
      ...baseNumUserIconStyles,
      padding: [1, 5, 0, 2],
      img: usersExitedIcon,
    },
    [shapeNames.numUsersText]: {
      ...baseNumUsersTextStyles,
      cursor: 'pointer',
      fontWeight: fontWeights.regular,
      fill: colors.textPrimary,
    },
    [shapeNames.numUsersPrimaryText]: {
      ...baseNumUsersTextStyles,
      fontWeight: fontWeights.semiBold,
      fill: colors.textPrimary,
    },
    [shapeNames.numUsersSecondaryText]: {
      ...baseNumUsersTextStyles,
      padding: 2,
      fontWeight: fontWeights.regular,
      fill: colors.secondaryText,
    },
    [shapeNames.numUsersEnteredPrimaryText]: {
      ...baseNumUsersTextStyles,
      fontWeight: fontWeights.semiBold,
    },
    [shapeNames.numUsersExitedPrimaryText]: {
      ...baseNumUsersTextStyles,
      fontWeight: fontWeights.semiBold,
    },
    [shapeNames.numUsersEnteredSecondaryText]: {
      ...baseNumUsersTextStyles,
      padding: 1,
      fontWeight: fontWeights.regular,
    },
    [shapeNames.numUsersExitedSecondaryText]: {
      ...baseNumUsersTextStyles,
      padding: 1,
      fontWeight: fontWeights.regular,
    },
    [shapeNames.numUsersWrapper]: {
      ...baseNumUsersStyles,
      cursor: 'pointer',
      y: 18,
    },
    [shapeNames.numUsersEnteredWrapper]: {
      ...baseNumUsersStyles,
      padding: [3, 6],
      fill: colors.backgroundTertiary,
      y: 5,
      labelText: 'Entered',
    },
    [shapeNames.numUsersExitedWrapper]: {
      ...baseNumUsersStyles,
      padding: [3, 6],
      fill: colors.backgroundTertiary,
      labelText: 'Exited',
      y: 33,
    },
    ...deleteStyles,
    ...noteIndicatorStyles,
    stateStyles: {
      // hover
      [ItemState.Active]: {
        [shapeNames.wrapper]: {
          lineWidth: 1,
        },
        ...deleteStylesActive,
      },
      // selected
      [ItemState.Selected]: {
        [shapeNames.wrapper]: {
          stroke: colors.grey80,
          lineWidth: 2,
        },
      },
      [ItemState.AIGenerated]: {
        [shapeNames.wrapper]: {
          stroke: colors.purple50,
          lineWidth: 2,
        },
      },
      [ItemState.HoverNumOfUser]: {
        [shapeNames.numUsersWrapper]: {
          stroke: colors.grey50,
          lineWidth: 1,
        },
      },
      [ItemState.HoverNumOfUserEntered]: {
        [shapeNames.numUsersEnteredWrapper]: {
          stroke: colors.grey50,
          lineWidth: 1,
        },
      },
      [ItemState.HoverNumOfUserExited]: {
        [shapeNames.numUsersExitedWrapper]: {
          stroke: colors.grey50,
          lineWidth: 1,
        },
      },
      // similar to Active, but doesn't show clickable elements (such as the
      // delete button)
      [ItemState.HighLight]: {
        [shapeNames.wrapper]: {
          lineWidth: 1,
        },
      },
      [ItemState.Error]: {
        [shapeNames.errorHighlight]: {
          // background
          fill: colors.red10,
          opacity: 0.5,

          // border
          stroke: colors.red50,
          lineWidth: 1,
          lineDash: [5],
          radius: 8,
          padding: [27, 13, 15, 13],
        },
      },
    },
  };
};
